import { ICheckedEntity } from '@se/constraint';
import { Json, JsonObject, JsonPrimitive } from '@se/cp.common';
import { DataBinding } from '@se/se.ui.html5';
import { isArray, isEmpty } from 'lodash-es';
import moment, { Moment } from 'moment';
import { Observable } from 'rxjs';

export type PropertyType = JsonPrimitive | Moment | ICpEntity | ICpEntity[];

export function isPropertyPrimitive(value: unknown): value is JsonPrimitive {
    if (isArray(value) && !isEmpty(value)) return value.every((e) => isPropertyPrimitive(e));

    return value === null || typeof value === 'boolean' || typeof value === 'number' || typeof value === 'string';
}

export function isPropertyLeaf(value: unknown): value is JsonPrimitive | Moment {
    if (isArray(value) && !isEmpty(value)) return value.every((e) => isPropertyLeaf(e));

    return isPropertyPrimitive(value) || moment.isMoment(value);
}

export type PropertyVisitor = (key: string, value: PropertyType) => boolean | void;

export interface ICpEntityFactory {
    isUndoEntity(entity: PropertyType | undefined): boolean;

    getAdvancedBindingKeys(binding: DataBinding): string[] | undefined;

    makeEntity(value: JsonPrimitive, binding?: DataBinding, parent?: ICpEntity): JsonPrimitive;

    makeEntity(value: JsonObject | Json[], binding?: DataBinding, parent?: ICpEntity): ICpEntity;

    makeEntity(value: Json, binding?: DataBinding, parent?: ICpEntity): PropertyType;

    makeEntityFromDataset(
        binding: DataBinding,
        parent?: ICpEntity,
        ...advancedBindingValues: (string | null)[]
    ): PropertyType;

    makeEntityFromPlaceholder(binding: DataBinding, parent?: ICpEntity): PropertyType;

    makeUndoEntity(entity: ICpEntity): ICpEntity;
}

export interface ICpEntity extends ICheckedEntity {
    readonly Id: string;

    parent?: ICpEntity;

    isModified: boolean;

    isModified$: Observable<boolean>;

    isValid$: Observable<boolean>;

    autoUpdatedPropertyList$: Observable<Record<string, boolean>>;

    isAutoUpdated: boolean;

    addAutoUpdated(property: string | undefined, autoUpdated: boolean): void;

    addEntityToList(path: DataBinding, value: ICpEntity, isVirtual?: boolean): void;

    destroy(): void;

    forOwn(fn: PropertyVisitor): void;

    getAllEntities(path: DataBinding): ICpEntity[] | undefined;

    getEntity(path: DataBinding, createMissingEntities?: boolean): ICpEntity | undefined;

    getSelectedEntity(path: DataBinding, createMissingEntities?: boolean): ICpEntity | undefined;

    getPropertyPrimitiveValue(propertyName: string): PropertyType;

    getPropertyValue(propertyName: string): PropertyType;

    getNestedPropertyValue(commaSeparatedPropertyName: string): PropertyType;

    getValue(path: DataBinding): PropertyType;

    hasProperty(propertyName: string): boolean;

    navigateToPath(path: DataBinding, createMissingEntities?: boolean): PropertyType | undefined;

    remove(entity?: ICpEntity): boolean;

    select(entity?: ICpEntity): void;

    setPropertyValue(propertyName: string, value: PropertyType, emitEvent?: boolean, autoUpdated?: boolean): void;

    setValue(path: DataBinding, value: PropertyType, emitEvent?: boolean, autoUpdated?: boolean): void;

    updatePath(): string;

    /**
     * Converte in una oggetto json il contenuto della entity.
     */
    toJSON(skipAutogeneratedId?: boolean): Json;
}

export interface ICpEntityList extends ICpEntity {
    getProperties(): ICpEntity[];

    getEntityIndex(entity: ICpEntity): number;
}

export const isCpEntityList = (value: any): value is ICpEntityList => typeof value?.getProperties === 'function';
